import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, LinearProgress, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient.ts';
import { useEffectiveStudentId } from '../hooks/useEffectiveStudentId.ts';
import { useTutorSession } from '../context/TutorSessionContext.tsx';
import { logTutorSessionEvent } from '../utils/tutorSessionEvents.ts';
import RichText from '../components/RichText.tsx';

type YearRow = { id: string; year: string | number };
type SubjectRow = { id: string; name: string; year_id: string | null };
type CategoryRow = {
  id: string;
  name: string;
  parent_category_id: string | null;
  subject_id: string | null;
};

type AssignmentRow = {
  lesson_id: string;
  assigned_at: string | null;
};

type Lesson = {
  id: string;
  title: string | null;
  created_at: string | null;
  updated_at?: string | null;
  category_id: string | null;
  subject_id: string | null;
  content: string | null;
  metadata: Record<string, any>;
  previewHtml: string;
  tagNames: string[];
  resolvedSubjectId: string | null;
  resolvedYearId: string | null;
  categoryPath: string | null;
  assigned_at: string | null;
};

const MAX_VISIBLE_TAGS = 3;
const PREVIEW_FALLBACK = '<p style="color:#64748b;font-size:12px;">No preview</p>';

const extractQuestionIdFromAttrs = (attrs: string): string | null => {
  const m = attrs.match(/\bid\s*=\s*["']([^"']+)["']/i);
  return m?.[1] ?? null;
};

const extractQuestionIdsFromHtml = (html: string | null | undefined): string[] => {
  if (!html) return [];
  const ids = new Set<string>();
  const rx = /<question\b([^>]*)\/?>/gi;
  let match: RegExpExecArray | null = null;
  while ((match = rx.exec(html)) !== null) {
    const id = extractQuestionIdFromAttrs(match[1] ?? '');
    if (id) ids.add(id);
  }
  return Array.from(ids);
};

const buildQuestionPreviewBlock = (questionId: string, promptHtml?: string): string => {
  if (promptHtml && promptHtml.trim().length > 0) {
    return `
      <div style="margin:6px 0;padding:8px 10px;border:1px dashed #93c5fd;border-radius:10px;background:#eff6ff;">
        <div style="font-size:11px;color:#475569;margin-bottom:6px;">Embedded question • #${questionId.slice(0, 8)}...</div>
        <div>${promptHtml}</div>
      </div>
    `;
  }
  return `
    <div style="margin:6px 0;padding:8px 10px;border:1px dashed #93c5fd;border-radius:10px;background:#eff6ff;color:#1e3a8a;font-size:12px;">
      Embedded question • #${questionId.slice(0, 8)}...
    </div>
  `;
};

const normalisePreviewHtml = (
  html: string | null | undefined,
  questionPromptById: Record<string, string>,
): string => {
  if (!html) return PREVIEW_FALLBACK;
  const replaceTag = (attrs: string) => {
    const qid = extractQuestionIdFromAttrs(attrs);
    if (!qid) return PREVIEW_FALLBACK;
    return buildQuestionPreviewBlock(qid, questionPromptById[qid]);
  };
  return html
    .replace(/<question\b([^>]*)>([\s\S]*?)<\/question>/gi, (_m, attrs) => replaceTag(attrs))
    .replace(/<question\b([^>]*)\/>/gi, (_m, attrs) => replaceTag(attrs));
};

const extractTagNames = (metadata: any): string[] => {
  if (!metadata || typeof metadata !== 'object') return [];
  const candidates = [metadata.tags, metadata.tagNames, metadata.tag_names];
  const out = new Set<string>();
  candidates.forEach((value) => {
    if (!Array.isArray(value)) return;
    value.forEach((item) => {
      if (typeof item === 'string' && item.trim()) out.add(item.trim());
      if (item && typeof item === 'object' && typeof item.name === 'string' && item.name.trim()) {
        out.add(item.name.trim());
      }
    });
  });
  return Array.from(out).sort((a, b) => a.localeCompare(b));
};

const formatAssignedAt = (value: string | null): string | null => {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString('en-AU', {
    dateStyle: 'medium',
    timeZone: 'Australia/Sydney',
  });
};

const AssignedLessonsPage: React.FC = () => {
  const navigate = useNavigate();
  const { tutorSession } = useTutorSession();
  const { studentId, loading: studentIdLoading } = useEffectiveStudentId();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [years, setYears] = useState<YearRow[]>([]);
  const [subjects, setSubjects] = useState<SubjectRow[]>([]);

  const yearById = useMemo(
    () => Object.fromEntries(years.map((y) => [y.id, y])) as Record<string, YearRow>,
    [years],
  );
  const subjectById = useMemo(
    () => Object.fromEntries(subjects.map((s) => [s.id, s])) as Record<string, SubjectRow>,
    [subjects],
  );

  useEffect(() => {
    if (studentIdLoading) return;
    (async () => {
      setLoading(true);
      setErr(null);

      if (!studentId) {
        setErr('Student record not found.');
        setLessons([]);
        setLoading(false);
        return;
      }

      const { data: student, error: studentErr } = await supabase
        .from('students')
        .select('company_id')
        .eq('id', studentId)
        .maybeSingle();
      if (studentErr || !student?.company_id) {
        setErr(studentErr?.message ?? 'Student company not found.');
        setLessons([]);
        setLoading(false);
        return;
      }

      const { data: assignmentRows, error: assignmentErr } = await supabase
        .from('lesson_assignments')
        .select('lesson_id, assigned_at')
        .eq('student_id', studentId);
      if (assignmentErr) {
        setErr(assignmentErr.message);
        setLessons([]);
        setLoading(false);
        return;
      }

      const assignments = (assignmentRows ?? []) as AssignmentRow[];
      const lessonIds = assignments.map((row) => row.lesson_id).filter(Boolean);
      if (lessonIds.length === 0) {
        setYears([]);
        setSubjects([]);
        setLessons([]);
        setLoading(false);
        return;
      }

      const { data: studentSubjects } = await supabase
        .from('student_subjects')
        .select('subject_id')
        .eq('student_id', studentId);
      const enrolledSubjectIds = (studentSubjects ?? [])
        .map((r: any) => r.subject_id as string)
        .filter(Boolean);

      let subjectQuery = supabase.from('subjects').select('id, name, year_id');
      if (enrolledSubjectIds.length) subjectQuery = subjectQuery.in('id', enrolledSubjectIds);
      const { data: subjectRows, error: subjectErr } = await subjectQuery;
      if (subjectErr) {
        setErr(subjectErr.message);
        setLessons([]);
        setLoading(false);
        return;
      }
      const downloadedSubjects = ((subjectRows ?? []) as any[]).map((s) => ({
        id: s.id,
        name: s.name,
        year_id: s.year_id ?? null,
      })) as SubjectRow[];
      setSubjects(downloadedSubjects);

      const allSubjectIds = downloadedSubjects.map((s) => s.id);
      let downloadedCategories: CategoryRow[] = [];
      if (allSubjectIds.length > 0) {
        const { data: rootCats } = await supabase
          .from('categories')
          .select('id, name, parent_category_id, subject_id')
          .in('subject_id', allSubjectIds)
          .is('parent_category_id', null);
        const allMap: Record<string, CategoryRow> = {};
        const queue: string[] = [];
        (rootCats ?? []).forEach((c: any) => {
          allMap[c.id] = c as CategoryRow;
          queue.push(c.id);
        });
        while (queue.length) {
          const batch = Array.from(new Set(queue.splice(0, 50)));
          const { data: kids } = await supabase
            .from('categories')
            .select('id, name, parent_category_id, subject_id')
            .in('parent_category_id', batch);
          (kids ?? []).forEach((c: any) => {
            if (!allMap[c.id]) {
              allMap[c.id] = c as CategoryRow;
              queue.push(c.id);
            }
          });
        }
        downloadedCategories = Object.values(allMap);
      }
      const downloadedCategoryById = Object.fromEntries(
        downloadedCategories.map((c) => [c.id, c]),
      ) as Record<string, CategoryRow>;
      const downloadedCategorySubjectById: Record<string, string | null> = {};
      downloadedCategories.forEach((cat) => {
        let cursor: CategoryRow | undefined = cat;
        let hops = 0;
        while (cursor && hops < 8) {
          if (cursor.subject_id) {
            downloadedCategorySubjectById[cat.id] = cursor.subject_id;
            return;
          }
          cursor = cursor.parent_category_id
            ? downloadedCategoryById[cursor.parent_category_id]
            : undefined;
          hops += 1;
        }
        downloadedCategorySubjectById[cat.id] = null;
      });
      const downloadedCategoryPathById: Record<string, string> = {};
      downloadedCategories.forEach((cat) => {
        const parts: string[] = [];
        let cursor: CategoryRow | undefined = cat;
        let hops = 0;
        while (cursor && hops < 8) {
          parts.unshift(cursor.name);
          cursor = cursor.parent_category_id
            ? downloadedCategoryById[cursor.parent_category_id]
            : undefined;
          hops += 1;
        }
        downloadedCategoryPathById[cat.id] = parts.join(' > ');
      });
      const downloadedSubjectById = Object.fromEntries(
        downloadedSubjects.map((s) => [s.id, s]),
      ) as Record<string, SubjectRow>;

      const visibleYearIds = Array.from(
        new Set(downloadedSubjects.map((s) => s.year_id).filter((id): id is string => Boolean(id))),
      );
      if (visibleYearIds.length > 0) {
        const { data: yearRows } = await supabase
          .from('years')
          .select('id, year')
          .in('id', visibleYearIds)
          .order('year', { ascending: true });
        setYears((yearRows ?? []) as YearRow[]);
      } else {
        setYears([]);
      }

      const { data: lessonRows, error: lessonErr } = await supabase
        .from('lessons')
        .select('id, title, created_at, updated_at, category_id, subject_id, content, metadata')
        .eq('company_id', student.company_id)
        .in('id', lessonIds);
      if (lessonErr) {
        setErr(lessonErr.message);
        setLessons([]);
        setLoading(false);
        return;
      }

      const assignedAtByLessonId = new Map<string, string | null>(
        assignments.map((row) => [row.lesson_id, row.assigned_at ?? null]),
      );

      const sourceLessons = (lessonRows ?? []) as any[];
      const questionIds = Array.from(
        new Set(sourceLessons.flatMap((l) => extractQuestionIdsFromHtml(l.content))),
      );
      const questionPromptById: Record<string, string> = {};
      if (questionIds.length > 0) {
        const { data: qs } = await supabase
          .from('questions')
          .select('id, prompt')
          .in('id', questionIds);
        (qs ?? []).forEach((q: any) => {
          if (q?.id && typeof q.prompt === 'string') questionPromptById[q.id] = q.prompt;
        });
      }

      const mapped: Lesson[] = sourceLessons
        .map((l) => {
          const categoryId = (l.category_id as string | null) ?? null;
          const subjectId =
            (l.subject_id as string | null) ??
            (categoryId ? downloadedCategorySubjectById[categoryId] ?? null : null);
          return {
            id: l.id,
            title: l.title ?? null,
            created_at: l.created_at ?? null,
            updated_at: l.updated_at ?? null,
            category_id: categoryId,
            subject_id: (l.subject_id as string | null) ?? null,
            content: l.content ?? null,
            metadata: (l.metadata ?? {}) as Record<string, any>,
            previewHtml: normalisePreviewHtml(l.content ?? null, questionPromptById),
            tagNames: extractTagNames(l.metadata),
            resolvedSubjectId: subjectId,
            resolvedYearId: subjectId ? downloadedSubjectById[subjectId]?.year_id ?? null : null,
            categoryPath: categoryId ? downloadedCategoryPathById[categoryId] ?? null : null,
            assigned_at: assignedAtByLessonId.get(l.id) ?? null,
          };
        })
        .sort(
          (a, b) =>
            new Date(b.assigned_at ?? 0).getTime() - new Date(a.assigned_at ?? 0).getTime(),
        );

      setLessons(mapped);
      setLoading(false);
    })();
  }, [studentId, studentIdLoading]);

  const contextForLesson = (lesson: Lesson) => {
    const chips: string[] = [];
    if (lesson.resolvedYearId && yearById[lesson.resolvedYearId]) chips.push(String(yearById[lesson.resolvedYearId].year));
    if (lesson.resolvedSubjectId && subjectById[lesson.resolvedSubjectId]) chips.push(subjectById[lesson.resolvedSubjectId].name);
    if (lesson.categoryPath) chips.push(lesson.categoryPath);
    return chips;
  };

  const secondaryLineForLesson = (lesson: Lesson) => {
    const topics = Number(lesson.metadata?.topicCount ?? lesson.metadata?.topics ?? 0);
    const questions = Number(lesson.metadata?.questionCount ?? lesson.metadata?.questions ?? 0);
    const minutes = Number(lesson.metadata?.estimatedMinutes ?? lesson.metadata?.minutes ?? 0);
    const parts: string[] = [];
    if (topics > 0) parts.push(`${topics} topics`);
    if (questions > 0) parts.push(`${questions} questions`);
    if (minutes > 0) parts.push(`~${minutes} min`);
    return parts.join(' • ');
  };

  const heading = useMemo(
    () => `Assigned Lessons${lessons.length > 0 ? ` (${lessons.length})` : ''}`,
    [lessons.length],
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">{heading}</Typography>
        <Tabs
          value="/lessons/assigned"
          onChange={(_e, value) => navigate(value)}
          sx={{
            minHeight: 40,
            '& .MuiTabs-indicator': { backgroundColor: 'primary.main' },
          }}
        >
          <Tab
            value="/lessons"
            label="Find Lessons"
            sx={{ textTransform: 'none', minHeight: 40, color: '#111827' }}
          />
          <Tab
            value="/lessons/assigned"
            label="Your Lessons"
            sx={{ textTransform: 'none', minHeight: 40, color: '#111827' }}
          />
        </Tabs>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {!loading && !err && lessons.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
          No lessons have been assigned to you yet.
        </Paper>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 2.75,
        }}
      >
        {lessons.map((lesson) => {
          const metaChips = contextForLesson(lesson);
          const secondary = secondaryLineForLesson(lesson);
          const assignedAtLabel = formatAssignedAt(lesson.assigned_at);
          return (
            <Paper
              key={lesson.id}
              variant="outlined"
              onClick={() => {
                if (tutorSession?.id) {
                  void logTutorSessionEvent(tutorSession.id, 'lesson_view', {
                    lesson_id: lesson.id,
                    title: lesson.title ?? '',
                    category_id: lesson.category_id ?? null,
                    viewed: true,
                    source: 'assigned_lessons_card_click',
                  });
                }
                navigate(`/lessons/${lesson.id}`);
              }}
              sx={{
                width: '100%',
                borderRadius: 2.5,
                cursor: 'pointer',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                border: '2px solid rgb(180, 180, 180)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'box-shadow 150ms ease, transform 120ms ease, border-color 120ms ease',
                '&:hover': {
                  boxShadow: 2,
                  transform: 'translateY(-2px)',
                  borderColor: 'primary.light',
                },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: 180,
                  bgcolor: 'grey.50',
                  borderBottom: (t) => `1px solid ${t.palette.divider}`,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    inset: 0,
                    p: 1,
                    transform: 'scale(0.56)',
                    transformOrigin: 'top left',
                    width: '180%',
                    height: '180%',
                    '& img': { maxWidth: '100%', height: 'auto' },
                    '& table': { width: '100%', borderCollapse: 'collapse' },
                    '& *': { maxWidth: '100%' },
                  }}
                >
                  <RichText html={lesson.previewHtml || PREVIEW_FALLBACK} />
                </Box>
                <Box
                  sx={{
                    pointerEvents: 'none',
                    position: 'absolute',
                    inset: 0,
                    background: `
                      linear-gradient(to top,
                        rgba(2, 6, 23, 0.1) -100%,
                        rgba(2, 6, 23, 0.0) 60%
                      )
                    `,
                  }}
                />
              </Box>

              <Box sx={{ px: 1.5, py: 1.25, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <MenuBookIcon color="action" fontSize="small" />
                  <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap title={lesson.title ?? ''}>
                    {lesson.title || 'Untitled'}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                  {metaChips.map((chip) => (
                    <Chip key={chip} size="small" label={chip} variant="outlined" />
                  ))}
                </Stack>

                {secondary && (
                  <Typography variant="body2" color="text.secondary">
                    {secondary}
                  </Typography>
                )}

                {assignedAtLabel && (
                  <Typography variant="body2" color="text.secondary">
                    Assigned {assignedAtLabel}
                  </Typography>
                )}

                {lesson.tagNames.length > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
                    {lesson.tagNames.slice(0, MAX_VISIBLE_TAGS).map((tag) => (
                      <Chip key={tag} size="small" label={tag} variant="outlined" />
                    ))}
                    {lesson.tagNames.length > MAX_VISIBLE_TAGS && (
                      <Chip size="small" label={`+${lesson.tagNames.length - MAX_VISIBLE_TAGS}`} variant="outlined" />
                    )}
                  </Stack>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default AssignedLessonsPage;
